import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '@/hooks/useCrm';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';

// Color band on left edge whose intensity reflects progress %.
// Goes from red (low) → amber → yellow → emerald (high).
const progressColor = (pct) => {
  if (pct >= 80) return '#10B981'; // emerald-500
  if (pct >= 60) return '#84CC16'; // lime-500
  if (pct >= 40) return '#FACC15'; // yellow-400
  if (pct >= 20) return '#FB923C'; // orange-400
  return '#EF4444'; // red-500
};

const StatCard = ({ label, value, accent, expanded, onClick, testid }) => (
  <button
    data-testid={testid || `stat-${label.toLowerCase()}`}
    onClick={onClick}
    className={`text-left rounded-2xl p-6 border ${accent} transition-shadow hover:shadow-md`}
  >
    <div className="flex items-start justify-between">
      <div className="text-xs font-semibold tracking-wide text-ink/60">{label}</div>
      {expanded ? <ChevronDown className="w-4 h-4 text-ink/40" /> : <ChevronRight className="w-4 h-4 text-ink/40" />}
    </div>
    <div className="font-display text-5xl font-bold text-ink mt-2 tracking-tight">{value}</div>
  </button>
);

const ActiveCard = ({ client, onClick }) => {
  const co = client.company || {};
  const days = client.days_remaining;
  const overdue = days !== null && days < 0;
  return (
    <button
      data-testid={`dashboard-card-${client.id}`}
      onClick={onClick}
      className="text-left bg-white rounded-2xl border border-mute/30 hover:border-primary/50 hover:shadow-card-hover transition-all overflow-hidden flex h-44"
    >
      {/* Left progress band: width is fixed, color reflects progress % */}
      <div
        data-testid={`progress-band-${client.id}`}
        className="w-3 shrink-0"
        style={{ backgroundColor: progressColor(client.progress_pct) }}
      />
      <div className="flex-1 px-5 py-4 flex flex-col">
        {/* Top: name + big % */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xl font-bold text-gray-900 truncate">
              {co.short_name || co.company_name}
            </div>
            <div className={`text-xs mt-1 ${overdue ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
              <span className="font-semibold">Deadline:</span>{' '}
              {days !== null ? (overdue ? `${Math.abs(days)} days overdue` : `${days} More Days`) : '—'}
            </div>
          </div>
          <div className="font-display text-3xl font-bold text-ink shrink-0 tracking-tight">
            {client.progress_pct}<span className="text-xl align-top">%</span>
          </div>
        </div>

        {/* Bottom: pending / status / remark */}
        <div className="mt-auto pt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div className="text-gray-700">
            <span className="font-semibold">Pending:</span> {client.pending_docs}/{client.total_docs}
          </div>
          <div className="text-gray-700 truncate">
            <span className="font-semibold">Status:</span> {client.status}
          </div>
          <div className="col-span-2 text-gray-600 italic truncate">
            <span className="font-semibold not-italic text-gray-700">Remark:</span> {client.remark || '—'}
          </div>
        </div>
      </div>
    </button>
  );
};

const SubmittedCard = ({ client, onClick }) => {
  const co = client.company || {};
  const days = client.days_since_submission;
  return (
    <button
      data-testid={`dashboard-card-${client.id}`}
      onClick={onClick}
      className="text-left bg-white rounded-2xl border border-mute/30 hover:border-primary/50 hover:shadow-card-hover transition-all px-5 py-4 flex flex-col h-44"
    >
      <div className="text-xl font-bold text-ink truncate">
        {co.short_name || co.company_name}
      </div>
      <div className="text-xs text-ink/60 mt-1">
        <span className="font-semibold text-ink/80">Submission:</span>{' '}
        {days === null ? '—' : days === 0 ? 'Today' : `${days} Day${days === 1 ? '' : 's'} Ago`}
      </div>

      <div className="mt-auto pt-3 space-y-1 text-xs">
        <div className="text-ink/70 truncate">
          <span className="font-semibold">Status:</span> {client.status}
        </div>
        <div className="text-ink/50 italic truncate">
          <span className="font-semibold not-italic text-ink/70">Remark:</span> {client.remark || '—'}
        </div>
      </div>
    </button>
  );
};

const CompletedCard = ({ client, onClick }) => {
  const co = client.company || {};
  return (
    <button
      data-testid={`dashboard-card-${client.id}`}
      onClick={onClick}
      className="text-left bg-white rounded-2xl border border-emerald-200/60 hover:border-emerald-400 hover:shadow-card-hover transition-all px-5 py-4 flex flex-col h-44"
    >
      <div className="text-xl font-bold text-ink truncate">
        {co.short_name || co.company_name}
      </div>
      <div className="text-xs text-emerald-700 mt-1 font-semibold">Completed</div>
      <div className="mt-auto pt-3 text-xs text-ink/60 truncate">
        {co.company_name}
      </div>
    </button>
  );
};

const Section = ({ title, items, render, emptyText, open, onToggle, testid }) => (
  <section className="mb-10" data-testid={testid}>
    <button
      data-testid={`${testid}-toggle`}
      onClick={onToggle}
      className="w-full flex items-center gap-3 mb-4 group"
    >
      {open ? <ChevronDown className="w-4 h-4 text-ink/50" /> : <ChevronRight className="w-4 h-4 text-ink/50" />}
      <h2 className="font-display text-lg font-semibold tracking-tight text-ink">{title}</h2>
      <div className="flex-1 h-px bg-mute/40" />
      <span className="text-sm text-ink/50">{items.length}</span>
    </button>
    {open && (
      items.length === 0 ? (
        <div className="text-sm text-gray-500 italic">{emptyText}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(render)}
        </div>
      )
    )}
  </section>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { data, loading } = useDashboard();
  const [openSection, setOpenSection] = useState({ active: true, submitted: true, completed: false });
  const open = (id) => navigate(`/clients/${id}`);
  const toggle = (key) => setOpenSection((s) => ({ ...s, [key]: !s[key] }));

  if (loading || !data) {
    return (
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm h-32 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const counts = data.counts;

  return (
    <div className="p-8" data-testid="dashboard">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-ink/60 mt-1">Welcome to RA License by Abroad Papers</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Active"    value={counts.active}    accent="border-blue-200/70 bg-blue-50/60"   expanded={openSection.active}    onClick={() => toggle('active')} />
        <StatCard label="Submitted" value={counts.submitted} accent="border-cyan-200/70 bg-cyan-50/60"   expanded={openSection.submitted} onClick={() => toggle('submitted')} />
        <StatCard label="Completed" value={counts.completed} accent="border-emerald-200/70 bg-emerald-50/50" expanded={openSection.completed} onClick={() => toggle('completed')} />
        <button
          data-testid="add-new-client-card"
          onClick={() => navigate('/clients/new')}
          className="rounded-2xl p-6 border-2 border-dashed border-mute/70 bg-white hover:border-primary hover:bg-blue-50/60 transition-colors flex flex-col items-center justify-center gap-2 text-ink/60 hover:text-primary"
        >
          <Plus className="w-8 h-8" />
          <div className="text-sm font-semibold tracking-wide">Add New</div>
        </button>
      </div>

      <Section
        title="Active" testid="section-active"
        items={data.active}
        render={(c) => <ActiveCard key={c.id} client={c} onClick={() => open(c.id)} />}
        emptyText="No active clients yet."
        open={openSection.active}
        onToggle={() => toggle('active')}
      />
      <Section
        title="Submitted" testid="section-submitted"
        items={data.submitted}
        render={(c) => <SubmittedCard key={c.id} client={c} onClick={() => open(c.id)} />}
        emptyText="No submissions in progress."
        open={openSection.submitted}
        onToggle={() => toggle('submitted')}
      />
      <Section
        title="Completed" testid="section-completed"
        items={data.completed}
        render={(c) => <CompletedCard key={c.id} client={c} onClick={() => open(c.id)} />}
        emptyText="No completed clients yet."
        open={openSection.completed}
        onToggle={() => toggle('completed')}
      />
    </div>
  );
};

export default Dashboard;
